/**
 * Pure grouping logic for the Sources list (ADR-049).
 *
 * Two independent, composable groupings over a patient's `type=source`
 * documents:
 *
 * 1. {@link groupSourcesByTitle} — same-titled documents are versions of one
 *    logical source (a corrected re-absorb, or two genuinely distinct
 *    same-titled encounters); only the newest renders by default, with every
 *    earlier one reachable via a disclosure (carried over from #72,
 *    unchanged here).
 * 2. {@link groupTitleGroupsByDimension} — title-groups are further grouped
 *    into folders along a **grouping dimension** (ADR-050): today that
 *    dimension is the persisted absorption `source_kind`
 *    ({@link kindDimension}), so a future encounter-based grouping can slot
 *    in as an alternate dimension without reshaping this module.
 */
import type { BlueprintDocument } from "../../types";
import {
  ASSESSMENTS_FOLDER_KEY,
  isAssessmentKind,
  sourceKindLabel,
} from "../../utils/sourceKindLabels";
import { parseEncounterDateFromTitle } from "./sourceDisplay";

/** One title's documents, newest first. */
export interface SourceTitleGroup {
  latest: BlueprintDocument;
  /** Earlier same-titled documents, newest-first — reachable via disclosure, never dropped. */
  earlier: BlueprintDocument[];
}

/**
 * Group a patient's source documents by title, newest-first within each group.
 *
 * Two `type=source` documents can legitimately share a title either because
 * a corrected re-absorb produced a second handle, or because two genuinely
 * distinct encounters happened to get the same deterministic title — either
 * way, every document must stay reachable.
 *
 * @param sources - The patient's `type=source` documents.
 * @returns One {@link SourceTitleGroup} per distinct title, in first-seen order.
 */
export function groupSourcesByTitle(
  sources: BlueprintDocument[],
): SourceTitleGroup[] {
  const byTitle = new Map<string, BlueprintDocument[]>();
  for (const doc of sources) {
    const group = byTitle.get(doc.title);
    if (group) {
      group.push(doc);
    } else {
      byTitle.set(doc.title, [doc]);
    }
  }
  const groups: SourceTitleGroup[] = [];
  for (const group of byTitle.values()) {
    const [latest, ...earlier] = [...group].sort((a, b) =>
      (b.created_at ?? "").localeCompare(a.created_at ?? ""),
    );
    groups.push({ latest, earlier });
  }
  return groups;
}

/** A grouping dimension's key + human label for one title-group's latest document. */
export interface GroupDimensionResult {
  key: string;
  label: string;
}

/**
 * A grouping dimension: derives a folder key + label from a document.
 *
 * The one implementation today is {@link kindDimension} (the absorption
 * source kind, ADR-049/050). Encounter grouping — deferred behind a
 * source→encounter data-model linkage — would slot in as a second
 * implementation of this same type, not a rewrite of the folder UI.
 */
export type GroupDimension = (doc: BlueprintDocument) => GroupDimensionResult;

/** The shipped grouping dimension: the persisted absorption source kind, grouping all assessments under "assessments". */
export const kindDimension: GroupDimension = (doc) => {
  if (isAssessmentKind(doc.source_kind)) {
    return {
      key: ASSESSMENTS_FOLDER_KEY,
      label: sourceKindLabel(ASSESSMENTS_FOLDER_KEY),
    };
  }
  return {
    key: doc.source_kind ?? "",
    label: sourceKindLabel(doc.source_kind),
  };
};

/** One grouping-dimension folder: a set of title-groups sharing a key. */
export interface SourceFolder {
  key: string;
  label: string;
  /** Title-groups in this folder, most-recent-ENCOUNTER first (undated last). */
  groups: SourceTitleGroup[];
  /** True when any document in this folder (latest or an earlier version) is not yet ingested. */
  hasPending: boolean;
  /** Total document count across every title-group in this folder (used for the count badge). */
  documentCount: number;
  /** Optional nested sub-folders (e.g. instrument folders inside the Assessments folder). */
  subfolders?: SourceFolder[];
}

/**
 * A sortable rank for encounter-date ordering: `[datedFlag, -encounterMs]`.
 *
 * `datedFlag` is 0 for a title-group whose latest document's title parses to an
 * encounter date and 1 otherwise, so dated groups always sort before undated
 * ones; `-encounterMs` makes dated groups sort newest-first. Undated groups
 * share `[1, 0]` and fall to the deterministic tie-break in
 * {@link compareByEncounterDateDesc}.
 */
function encounterRank(group: SourceTitleGroup): [number, number] {
  const parsed = parseEncounterDateFromTitle(group.latest.title);
  return parsed ? [0, -parsed.getTime()] : [1, 0];
}

/**
 * Order two title-groups by their PARSED ENCOUNTER DATE, newest-first, with
 * unparseable-date groups sorted deterministically LAST (ADR-049 amendment /
 * ADR-056 D4).
 *
 * The encounter date (parsed from the title) is the clinically meaningful
 * "when"; `created_at` (the absorption timestamp) is only a stable tie-break for
 * two groups with the same encounter date or both undated — never the primary
 * key. A bulk warehouse pull shares one absorption day, so ordering by
 * `created_at` would put a folder's rows in a non-chronological order under
 * dates that don't ascend, and collapse D4's year buckets into one absorption
 * year. An undated group is placed LAST (not dropped, not random) so its
 * fail-visible D1 row ("Date unavailable") reads as deliberately trailing.
 *
 * @param a - The first title-group.
 * @param b - The second title-group.
 * @returns A comparator result (negative if `a` sorts first).
 */
export type SortField = "date" | "alphabetical";
export type SortDirection = "asc" | "desc";

export interface SourceGroupSortOptions {
  sortField?: SortField;
  sortDirection?: SortDirection;
}

/**
 * Order two title-groups by their PARSED ENCOUNTER DATE.
 *
 * @param a - The first title-group.
 * @param b - The second title-group.
 * @param direction - "desc" (default, newest-first) or "asc" (oldest-first). Undated groups always sort last.
 * @returns A comparator result.
 */
export function compareByEncounterDate(
  a: SourceTitleGroup,
  b: SourceTitleGroup,
  direction: SortDirection = "desc",
): number {
  const [aDated, aMs] = encounterRank(a);
  const [bDated, bMs] = encounterRank(b);
  if (aDated !== bDated) return aDated - bDated; // undated always last
  if (aMs !== bMs) {
    return direction === "asc" ? bMs - aMs : aMs - bMs;
  }
  const byCreated = (b.latest.created_at ?? "").localeCompare(
    a.latest.created_at ?? "",
  );
  if (byCreated !== 0) {
    return direction === "asc" ? -byCreated : byCreated;
  }
  return a.latest.id.localeCompare(b.latest.id);
}

export function compareByEncounterDateDesc(
  a: SourceTitleGroup,
  b: SourceTitleGroup,
): number {
  return compareByEncounterDate(a, b, "desc");
}

export function compareByAlphabetical(
  a: SourceTitleGroup,
  b: SourceTitleGroup,
  direction: SortDirection = "asc",
): number {
  const comp = a.latest.title.localeCompare(b.latest.title, undefined, {
    sensitivity: "base",
  });
  if (comp !== 0) {
    return direction === "desc" ? -comp : comp;
  }
  return compareByEncounterDateDesc(a, b);
}

function compareFolders(
  a: SourceFolder,
  b: SourceFolder,
  field: SortField,
  direction: SortDirection,
): number {
  if (field === "alphabetical") {
    const comp = a.label.localeCompare(b.label, undefined, {
      sensitivity: "base",
    });
    if (comp !== 0) {
      return direction === "desc" ? -comp : comp;
    }
    return compareByEncounterDate(a.groups[0], b.groups[0], "desc");
  }
  return compareByEncounterDate(a.groups[0], b.groups[0], direction);
}

/**
 * Group title-groups into folders along a dimension, ordered according to sort options.
 *
 * Folders with no members are never produced (a folder always has at least
 * one title-group). A folder with exactly one title-group is meant to be
 * rendered as a plain row, not folder chrome (decision 4) — that choice is
 * made by the caller/renderer, not this pure function.
 *
 * @param titleGroups - The patient's source title-groups.
 * @param dimension - The grouping dimension (kind today; encounter later).
 * @param options - Optional sort field ("date" | "alphabetical") and direction ("asc" | "desc").
 * @returns Folders ordered according to the chosen sort mode.
 */
export function groupTitleGroupsByDimension(
  titleGroups: SourceTitleGroup[],
  dimension: GroupDimension,
  options: SourceGroupSortOptions = {},
): SourceFolder[] {
  const { sortField = "date", sortDirection = "desc" } = options;
  const byKey = new Map<
    string,
    { label: string; groups: SourceTitleGroup[] }
  >();
  for (const group of titleGroups) {
    const { key, label } = dimension(group.latest);
    const bucket = byKey.get(key);
    if (bucket) {
      bucket.groups.push(group);
    } else {
      byKey.set(key, { label, groups: [group] });
    }
  }

  const groupComparator =
    sortField === "alphabetical"
      ? (a: SourceTitleGroup, b: SourceTitleGroup) =>
          compareByAlphabetical(a, b, sortDirection)
      : (a: SourceTitleGroup, b: SourceTitleGroup) =>
          compareByEncounterDate(a, b, sortDirection);

  const folders: SourceFolder[] = [];
  for (const [key, { label, groups }] of byKey) {
    const sortedGroups = [...groups].sort(groupComparator);
    const hasPending = sortedGroups.some(
      (g) =>
        g.latest.ingestion_status === "not_ingested" ||
        g.earlier.some((doc) => doc.ingestion_status === "not_ingested"),
    );
    const documentCount = sortedGroups.reduce(
      (sum, g) => sum + 1 + g.earlier.length,
      0,
    );

    let subfolders: SourceFolder[] | undefined;
    if (key === ASSESSMENTS_FOLDER_KEY) {
      // Group assessment title-groups by instrument kind
      const byInstrument = new Map<
        string,
        { label: string; groups: SourceTitleGroup[] }
      >();
      for (const group of sortedGroups) {
        const instKey = group.latest.source_kind ?? "unknown";
        const instLabel = sourceKindLabel(group.latest.source_kind);
        const bucket = byInstrument.get(instKey);
        if (bucket) {
          bucket.groups.push(group);
        } else {
          byInstrument.set(instKey, { label: instLabel, groups: [group] });
        }
      }
      subfolders = [];
      for (const [
        instKey,
        { label: instLabel, groups: instGroups },
      ] of byInstrument) {
        const sortedInstGroups = [...instGroups].sort(groupComparator);
        const instHasPending = sortedInstGroups.some(
          (g) =>
            g.latest.ingestion_status === "not_ingested" ||
            g.earlier.some((doc) => doc.ingestion_status === "not_ingested"),
        );
        const instDocCount = sortedInstGroups.reduce(
          (sum, g) => sum + 1 + g.earlier.length,
          0,
        );
        subfolders.push({
          key: instKey,
          label: instLabel,
          groups: sortedInstGroups,
          hasPending: instHasPending,
          documentCount: instDocCount,
        });
      }
      subfolders.sort((a, b) => compareFolders(a, b, sortField, sortDirection));
    }

    folders.push({
      key,
      label,
      groups: sortedGroups,
      hasPending,
      documentCount,
      ...(subfolders ? { subfolders } : {}),
    });
  }

  return folders.sort((a, b) => compareFolders(a, b, sortField, sortDirection));
}

/** Sentinel year-group label for title-groups with no parseable encounter date. */
export const UNDATED_YEAR_LABEL = "Undated";

/** One year sub-group inside a long folder (ADR-056 / decision D4). */
export interface SourceYearGroup {
  /** The four-digit year (e.g. "2026"), or {@link UNDATED_YEAR_LABEL}. */
  label: string;
  /** Total document count across this year's title-groups (siblings included). */
  documentCount: number;
  /** The title-groups in this year, most-recent-first. */
  groups: SourceTitleGroup[];
}

/**
 * Sub-group a folder's title-groups by the year of their encounter date (D4).
 *
 * A title-group's year is parsed from its latest document's title (never
 * `created_at`, the absorption date). Groups with no parseable encounter date
 * fall into a single trailing {@link UNDATED_YEAR_LABEL} bucket rather than
 * being dropped. Real years sort descending (newest first); the undated bucket
 * is always last.
 *
 * @param groups - The folder's title-groups (already most-recent-first).
 * @returns One {@link SourceYearGroup} per distinct year, newest-first, undated last.
 */
export function subgroupTitleGroupsByYear(
  groups: SourceTitleGroup[],
): SourceYearGroup[] {
  const byYear = new Map<string, SourceTitleGroup[]>();
  for (const group of groups) {
    const parsed = parseEncounterDateFromTitle(group.latest.title);
    const label = parsed ? String(parsed.getFullYear()) : UNDATED_YEAR_LABEL;
    const bucket = byYear.get(label);
    if (bucket) {
      bucket.push(group);
    } else {
      byYear.set(label, [group]);
    }
  }
  const yearGroups: SourceYearGroup[] = [];
  for (const [label, yearGroupList] of byYear) {
    const documentCount = yearGroupList.reduce(
      (sum, g) => sum + 1 + g.earlier.length,
      0,
    );
    yearGroups.push({ label, documentCount, groups: yearGroupList });
  }
  return yearGroups.sort(compareYearLabels);
}

/** Order year-group labels newest-first, with the undated bucket always last. */
function compareYearLabels(a: SourceYearGroup, b: SourceYearGroup): number {
  if (a.label === UNDATED_YEAR_LABEL) return 1;
  if (b.label === UNDATED_YEAR_LABEL) return -1;
  return b.label.localeCompare(a.label);
}

/**
 * Count the distinct real (non-undated) encounter years across title-groups.
 *
 * Used to decide whether year sub-grouping is worthwhile — sub-grouping a
 * folder that spans only one year (or whose titles carry no dates) adds a
 * pointless single header, so the caller requires at least two (ADR-056).
 *
 * @param groups - The folder's title-groups.
 * @returns The number of distinct parseable encounter years.
 */
export function distinctEncounterYears(groups: SourceTitleGroup[]): number {
  return subgroupTitleGroupsByYear(groups).filter(
    (y) => y.label !== UNDATED_YEAR_LABEL,
  ).length;
}
